import { defineComponent, reactive, SetupContext, readonly, ref } from '@vue/composition-api';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';              // フィルタ用共通コンポーネント
import { TableFilterDefs, TableFilters } from '../common/common-table-filter.component';
import { InformationList, TableHeader, ColumnMenu } from "@/shared/type";
import SystemConst from '@/shared/system-const';
import { DataOptions } from 'vuetify';
import HttpStatus from 'http-status-codes';
import CommonFunction from '@/shared/common-function';
import vueRouter from '@/router/index';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                               // Windowサイズ
    informationDisplayTableHeaders: TableHeader[];                      // お知らせ一覧ヘッダ情報
    informationListItem: InformationList[];                             // お知らせ一覧情報
    publicationSettingDialog: ColumnMenu                                // 公開設定ダイアログ
    totalCount: number;                                                 // お知らせ一覧の総件数
    pageOptions: DataOptions;                                           // ページ情報を保存する変数
    isLoading: boolean;                                                 // テーブルがロード中: true ロード中でない: false
    filterDefs: TableFilterDefs;                                        // フィルタ定義
    filters: TableFilters;                                              // フィルタ条件
    visibleRowMenu: ColumnMenu;                                         // 行メニュー表示切替
}

/**
 * お知らせ一覧（管理）画面コンポーネント
 */
export default defineComponent({
    components: { CommonTableFilter, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const tableHeaderClass: string[] = ['tableHeader subtitle-2'];  // お知らせ一覧のヘッダ色
        const tableHeight = SystemConst.TABLE_HEIGHT.INFORMATION_LIST;  // テーブル高さ調整用変数
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;           // 1ページあたりの行数候補（10, 50, 100）
        const pageNation = ref<number>(1);                              // ページ番号（状態管理用）
        const itemsPerPage = ref<number>(linesPerPage[1]);              // 1ページあたりの行数（状態管理用、デフォルト値：50）
        const sortBy = ref<string[]>([]);                               // ソートカラム（状態管理用）
        const sortDesc = ref<boolean[]>([]);                            // ソート順（状態管理用）
        let isPageLastData = false;                                     // ページの最終データフラグ
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;        // テーブルのデータ取得中に表示するテキスト
        const commonRef = ref<any>(null);                               // メッセージダイアログ（HTTP通信失敗のエラー画面遷移用）
        const filterRef = ref<any>(null);                               // フィルタコンポーネント
        const router = vueRouter;                                       // ルータ

        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                 // ウィンドウサイズ
            informationDisplayTableHeaders: [],                         // お知らせ一覧ヘッダー
            informationListItem: [],                                    // お知らせ一覧情報
            publicationSettingDialog: {},                               // 公開設定ダイアログ
            totalCount: 0,                                              // お知らせ一覧の総件数
            pageOptions: {                                              // ページ情報を保存する変数
                page: 1, itemsPerPage: linesPerPage[1], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            isLoading: false,                                           // テーブルがロード中: true ロード中でない: false
            filterDefs: {                                               // フィルタ定義
                'freeWord': { title: 'フリーワード', types: 'FreeWord' },
                'publicationRange': { title: '公開範囲', types: 'CheckBox' },
                'severity': { title: '重要度', types: 'CheckBox' },
                'sender': { title: '発信者', types: 'KeyWordPartialMatch' },
                'publicationDate': { title: '掲載期間', types: 'PublicationDate' },
                'publicState': { title: '公開状態', types: 'CheckBox' }
            },
            filters: {},                                                // フィルタ条件
            visibleRowMenu: {}                                          // 行メニュー表示切替
        });

        // お知らせ一覧ヘッダ情報
        const informationDisplayTableHeaders: TableHeader[] = [
            {
                text: '公開範囲',
                value: 'publicationRange',
                width: '22%',
                class: tableHeaderClass
            },
            {
                text: '重要度',
                value: 'severity',
                width: '6%',
                class: tableHeaderClass
            },
            {
                text: 'タイトル',
                value: 'title',
                width: '24%',
                class: tableHeaderClass
            },

            {
                text: '発信者',
                value: 'sender',
                width: '13%',
                class: tableHeaderClass
            },
            {
                text: '掲載期間',
                value: 'publicationDate',
                width: '22%',
                class: tableHeaderClass
            },
            {
                text: '公開状態',
                value: 'publicState',
                width: '9%',
                class: tableHeaderClass
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];

        // フィルタバリデーション
        // フリーワード
        const freeWordRules = [
            (v: any) => CommonFunction.checkLength(
                v,
                SystemConst.INFORMATION_LIST_MAX_LENGTH.FREE_WORD,
                'フリーワード'
            )
        ];
        // 発信者
        const senderRules = [
            (v: any) => CommonFunction.checkLength(
                v,
                SystemConst.INFORMATION_LIST_MAX_LENGTH.SENDER,
                '発信者'
            )
        ];

        /**
         * 初期表示
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-format-list-bulleted');

            // mainTitleを「お知らせ一覧」に変更
            ctx.root.$store.commit('setMainTitle', 'お知らせ一覧');

            // storeからフィルター条件をすべて復元
            state.filters = ctx.root.$store.state.information.filters;

            // storeから前回のページ番号、1ページあたりの行数を復元
            pageNation.value = ctx.root.$store.state.information.pageNation;
            itemsPerPage.value = ctx.root.$store.state.information.itemsPerPage;

            // storeから前回のソートキー、ソート順を復元
            sortBy.value = ctx.root.$store.state.information.sortBy;
            sortDesc.value = ctx.root.$store.state.information.sortDesc;

            // storeからページの最終データフラグを復元
            isPageLastData = ctx.root.$store.state.information.isPageLastData;
            // storeのページの最終データフラグをfalseに設定する
            ctx.root.$store.commit('information/setIsPageLastData', false);

            // isPageLastDataがtrueの場合、ページ番号を-1する
            if (isPageLastData) {
                pageNation.value -= 1;
                state.pageOptions.page -= 1;
            }

            // フィルタ情報を初期設定
            // awaitで処理の完了を待つ
            await initFilter();

            // ヘッダ設定
            state.informationDisplayTableHeaders = informationDisplayTableHeaders;

            // 前回のフィルタに対するID格納用の配列
            const filterIdList = [];
            // 前回のフィルタ条件格納用の配列
            const filterParameters = [];

            // state.filtersのプロパティ(key)で以下を繰り返し、有効なフィルタだけを抽出
            for (const key in state.filters) {
                // state.filtersのプロパティにkeyがある場合
                if (Object.prototype.hasOwnProperty.call(state.filters, key)) {
                    const val = state.filters[key];
                    // 有効なフィルタである場合
                    if (val.isEnabled) {
                        // フィルタのID(key)とフィルタ条件を保存
                        filterIdList.push(key);
                        filterParameters.push(val.condition);
                    }
                }
            }

            // 復元された有効なフィルタがある場合
            if (filterIdList.length !== 0) {
                // setAllFiltersで有効なフィルタをすべてstateに設定
                filterRef.value.setAllFilters(filterIdList, filterParameters);
            } else {
                // お知らせ一覧情報を設定
                updateList();
            }
        }

        /**
         * フィルタ情報を取得・設定
         */
        async function initFilter() {
            // お知らせ一覧フィルタ情報APIでフィルタに表示する情報を取得
            // GET /api/information/filter-conditions
            // パラメータ：なし
            const response = await ctx.root.$store.dispatch('information/getFilterConditions');

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // 以下の項目にAPIで取得したフィルタ情報を設定
            // 公開範囲
            state.filterDefs['publicationRange'].chkItems = response.data.publicationRange;
            // 重要度
            state.filterDefs['severity'].chkItems = response.data.severity;
            // 公開状態
            state.filterDefs['publicState'].chkItems = response.data.publicState;

            // 以下の項目にフィルタバリデーションを設定
            // フリーワード
            state.filterDefs['freeWord'].validator = freeWordRules;
            // 発信者
            state.filterDefs['sender'].validator = senderRules;
        }

        /**
         * お知らせ一覧の表示更新
         */
        async function updateList() {
            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // ページの最終データフラグをfalseに設定する
            isPageLastData = false;

            // 現在のページ情報/ソート情報、フィルタ条件をストアに保存
            // 詳細、登録、編集、削除画面から本画面に戻ってきたときに復元される
            ctx.root.$store.commit('information/setPage', pageNation.value);
            ctx.root.$store.commit('information/setItemsPerPage', itemsPerPage.value);
            ctx.root.$store.commit('information/setFilters', state.filters);
            ctx.root.$store.commit('information/setSortBy', sortBy.value);
            ctx.root.$store.commit('information/setSortDesc', sortDesc.value);

            // makeListParametersでAPIパラメータを作成する
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // お知らせ一覧取得APIでデータを取得する
            // GET /api/information
            // パラメータ：ページ番号
            //            1ページあたりの行数
            //            ソートキー
            //            ソート順
            //            フィルタ条件
            const response = await ctx.root.$store.dispatch('information/getInformation',
                {
                    page: params['page'],
                    itemsPerPage: params['itemsPerPage'],
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams']
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する
                state.isLoading = false;

                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // お知らせ一覧のデータをstate.informationListItemへ設定する
            state.informationListItem = response.data.informationLists;

            // 総検索件数をstate.totalCountへ設定する
            state.totalCount = response.data.totalCount;

            // 取得件数が1件かつ総データ件数が1件より多い場合、isPageLastDataをtrueにする
            if (state.informationListItem.length === 1 && state.totalCount > 1) {
                isPageLastData = true;
            }

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;

            // ページ番号保持フラグをfalseに設定する
            ctx.root.$store.state.information.isSavePage = false;
        }

        /**
         * ページ情報/ソート情報変更に対するイベント
         * @param options ページ情報/ソート情報
         */
        function updateOptions(options: DataOptions) {
            // ページ情報をstateへ保存する
            state.pageOptions = options;

            // 状態管理用変数とoptionsの中身と比較して、いずれも変更されていなかった場合
            if (pageNation.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                // 処理を終了する
                return;
            }

            // ページ番号、1ページあたりの行数を設定する
            pageNation.value = options.page;
            itemsPerPage.value = options.itemsPerPage;

            // ソートキー、ソート順を設定する
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // お知らせ一覧を更新する
            updateList();
        }

        /**
         * フィルタ設定によるイベント
         * 
         * @param filters フィルタ条件
         */
        function updateFilters(filters: TableFilters) {
            // filtersをstateへ保存する
            state.filters = filters;

            // ページ番号保持フラグがfalseの場合
            if (!ctx.root.$store.state.information.isSavePage) {
                // ページ番号を1へ戻す
                state.pageOptions.page = 1;
                pageNation.value = 1;
            }

            // お知らせ一覧を更新する
            updateList();
        }

        /**
         * お知らせ一覧添付ファイルダウンロード
         * 
         * @param informationId お知らせID
         * @param fileSequence ファイルシーケンス
         */
        async function download(informationId: string, fileSequence: number) {
            // お知らせ一覧添付ファイルダウンロードAPIでファイルをダウンロードする
            // GET /api/information/{informationId}/{fileSequence}
            // パラメータ：お知らせID
            //            ファイルシーケンス
            const response = await ctx.root.$store.dispatch('information/download',
                {
                    informationId: informationId,
                    fileSequence: fileSequence
                }
            );

            // HTTPステータスコードが200以外の場合、エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                // エラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }
        }

        /**
         * 公開設定変更処理
         * 
         * @param informationId お知らせID
         * @param isPublic 公開フラグ
         */
        async function changePublication(informationId: string, isPublic: boolean) {
            // isPublicを反転してAPI通信用にisPublicを数値に変換する（isPublicがtrue：0、isPublicがfalse：1）
            const changeIsPublic = Number(!isPublic);

            // 公開設定変更APIを呼ぶ
            //   PUT /api/information/publication-setting/{informationId}
            // パラメータ：お知らせID
            //            公開設定変更値
            const response = await ctx.root.$store.dispatch('information/changePublication',
                {
                    informationId: informationId,
                    changeIsPublic: changeIsPublic
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // お知らせ一覧を表示更新する
            updateList();
        }

        /**
         * お知らせ詳細画面に遷移する
         */
        function navigationDetail(informationId: string) {
            // storeのページ番号保持フラグをtrueに設定する
            ctx.root.$store.state.information.isSavePage = true;

            // お知らせ詳細画面に遷移する
            router.push('/information/detail/' + informationId);
        }

        /**
         * お知らせ登録画面へ遷移する
         */
        function navigationAdd() {
            router.push('/information/add');
        }

        /**
         * お知らせ編集画面へ遷移する
         */
        function navigationUpdate(informationId: string) {
            router.push('/information/edit/' + informationId);
        }

        /**
         * お知らせ削除画面へ遷移する
         */
        function navigationDelete(informationId: string) {
            // isPageLastDataをstoreに保存する
            ctx.root.$store.commit('information/setIsPageLastData', isPageLastData);

            // ページ番号保持フラグをstoreに保存する
            ctx.root.$store.commit('information/setIsSavePage', true);

            router.push('/information/delete/' + informationId);
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state: readonly(state),
            tableHeight,
            linesPerPage,
            pageNation,
            itemsPerPage,
            sortBy,
            sortDesc,
            tableLoadingText,
            commonRef,
            filterRef,
            onResize,
            updateOptions,
            updateFilters,
            download,
            changePublication,
            navigationDetail,
            navigationAdd,
            navigationUpdate,
            navigationDelete
        };
    }
});
